import { Activity, CheckCircle2, Crosshair, Layers, Lock, Map, Target } from "lucide-react"
import { cn } from "@/lib/utils"

const STEP_META = [
  { title: "Set Investment Target", Icon: Target },
  { title: "Select Strategy", Icon: Layers },
  { title: "Select Area", Icon: Map },
  { title: "Capture Area Dynamics", Icon: Activity },
  { title: "Set Deal Criteria", Icon: Crosshair },
]

export function ActivationProgressBar({ currentStep, totalSteps = 5, onStepClick }) {
  const completed = Math.min(currentStep - 1, totalSteps)
  const percent = Math.round((completed / totalSteps) * 100)
  const fillWidth = Math.min(100, (completed / (totalSteps - 1)) * 100)

  return (
    <div className="px-6 py-4">
      <div className="mx-auto max-w-[1600px]">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-bold text-foreground">Activation Sequence</h2>
          <span className="rounded-md border border-primary/20 bg-primary/10 px-2 py-1 font-mono text-xs text-primary">
            {percent}%
          </span>
        </div>

        <div className="relative flex items-center gap-2">
          <div className="absolute left-0 right-0 top-5 z-0 h-0.5 bg-muted" />
          <div
            className="absolute left-0 top-5 z-0 h-0.5 bg-primary transition-all duration-700 ease-in-out"
            style={{ width: `${fillWidth}%` }}
          />

          {STEP_META.map(({ title, Icon }, i) => {
            const stepNumber = i + 1
            const isCompleted = stepNumber < currentStep
            const isActive = stepNumber === currentStep
            const isLocked = stepNumber > currentStep

            return (
              <button
                key={stepNumber}
                type="button"
                onClick={() => { if (!isLocked) onStepClick(stepNumber) }}
                disabled={isLocked}
                className={cn(
                  "relative z-10 flex flex-1 flex-col items-center gap-2 transition-all",
                  isLocked ? "cursor-not-allowed opacity-40" : "cursor-pointer hover:opacity-80",
                )}
              >
                <div
                  className={cn(
                    "flex h-10 w-10 items-center justify-center rounded-full border-2 bg-card transition-colors",
                    isActive && "border-primary bg-primary/10 text-primary shadow-[0_0_15px_-3px_var(--color-primary)]",
                    isCompleted && "border-primary bg-primary text-primary-foreground",
                    !isActive && !isCompleted && "border-border text-muted-foreground",
                  )}
                >
                  {isCompleted ? (
                    <CheckCircle2 className="h-5 w-5" />
                  ) : isLocked ? (
                    <Lock className="h-4 w-4" />
                  ) : (
                    <Icon className="h-4 w-4" />
                  )}
                </div>

                <span
                  className={cn(
                    "hidden text-center text-xs font-medium leading-tight sm:block",
                    isActive && "font-bold text-foreground",
                    isCompleted && "text-foreground",
                    isLocked && "text-muted-foreground",
                  )}
                >
                  {title}
                </span>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
